import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import DepartmentModal from './DepartmentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import DepartmentDetailModal from './DepartmentDetailModal';
import TenantAdminLayout from '../Layout/TenantAdminLayout';

const Department = (props) => {
  const location = useLocation();
  const effectiveTenantId = props.tenantId ?? location.state?.tenantId;
  const effectiveToken = props.token ?? location.state?.token;
  const createdBy = location.state?.userId ?? null;
  const effectiveUserRole = props.userRole ?? localStorage.getItem('userRole') ?? 'tenant_admin';

  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState(null);

  // Modal display states
  const [modalState, setModalState] = useState({
    showCreateModal: false,
    showEditModal: false,
    editingDepartment: null,
    saving: false
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    departmentToDelete: null,
    deleting: false
  });

  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    department: null
  });

  // Authorization setup
  const authHeaders = useMemo(() => {
    const headers = { Accept: 'application/json' };
    if (effectiveToken) headers.Authorization = `Bearer ${effectiveToken}`;
    return headers;
  }, [effectiveToken]);

  // Load department lists from API
  const fetchDepartments = useCallback(async () => {
    if (!effectiveTenantId) {
      setDepartments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/departments/${effectiveTenantId}`, { headers: authHeaders });
      const list = (res?.data?.departments ?? []).map(d => ({
        ...d,
        facultyCount: Array.isArray(d.facultyIds) ? d.facultyIds.length : 0
      }));
      setDepartments(list);
      loadDetailedStats(list);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to fetch departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, authHeaders]);

  // Load faculty stats per department
  const loadDetailedStats = async (deptList) => {
    const statsPromises = deptList.map(async (dept) => {
      const deptId = dept._id || dept.id;
      if (!deptId) return null;

      try {
        const facultyRes = await api.get(`/api/departments/${dept.tenantId}/${deptId}/faculty`);
        const facultyCount = Array.isArray(facultyRes?.data?.faculty) ? facultyRes.data.faculty.length : 0;
        return { deptId, facultyCount };
      } catch (err) {
        console.error(`Failed to load stats for department ${deptId}:`, err);
        return { deptId, facultyCount: 0 };
      }
    });

    const statsResults = await Promise.all(statsPromises);
    
    setDepartments(prev => prev.map(dept => {
      const deptId = dept._id || dept.id;
      const stats = statsResults.find(s => s && s.deptId === deptId);
      if (stats) {
        return {
          ...dept,
          facultyCount: stats.facultyCount
        };
      }
      return dept;
    }));
  };

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // View management controls
  const openCreateModal = () => {
    setModalState({
      showCreateModal: true,
      showEditModal: false,
      editingDepartment: null,
      saving: false
    });
  };

  const openEditModal = (dept) => {
    setModalState({
      showCreateModal: false,
      showEditModal: true,
      editingDepartment: dept,
      saving: false
    });
  };

  const closeModal = () => {
    setModalState({
      showCreateModal: false,
      showEditModal: false,
      editingDepartment: null,
      saving: false
    });
  };

  const setSaving = (saving) => {
    setModalState(prev => ({ ...prev, saving }));
  };

  const openDeleteModal = (department) => {
    setDeleteModal({
      isOpen: true,
      departmentToDelete: department,
      deleting: false
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      departmentToDelete: null,
      deleting: false
    });
  };

  const openDetailsModal = (department) => {
    setDetailsModal({ isOpen: true, department });
  };

  const closeDetailsModal = () => {
    setDetailsModal({ isOpen: false, department: null });
  };

  const handleCreateSave = async ({ name }) => {
    if (!effectiveTenantId || !createdBy) {
      throw new Error('Missing tenant or user information.');
    }
    
    setSaving(true);
    try {
      const body = { tenantId: effectiveTenantId, name, createdBy };
      const res = await api.post('/api/add-department', body, { headers: authHeaders });

      const createdDept = res?.data?.department || {
        _id: res?.data?.departmentId || `temp-${Date.now()}`,
        name,
        tenantId: effectiveTenantId,
        createdBy,
        facultyIds: [],
        facultyCount: 0,
        createdAt: new Date().toISOString()
      };

      setDepartments(prev => [createdDept, ...prev]);
      return createdDept;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Create failed';
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSave = async ({ name }) => {
    if (!modalState.editingDepartment) {
      throw new Error('No department selected for editing.');
    }
    
    setSaving(true);
    try {
      const deptId = modalState.editingDepartment._id || modalState.editingDepartment.id;
      const updateData = { tenantId: effectiveTenantId, name };
      
      const res = await api.put(`/api/edit-department/${deptId}`, updateData, { headers: authHeaders });

      const updatedDepartment = res?.data?.department || {
        ...modalState.editingDepartment,
        name,
        updatedAt: new Date().toISOString()
      };

      setDepartments(prev => prev.map(d => {
        const id = d._id || d.id;
        return String(id) === String(deptId) ? updatedDepartment : d;
      }));

      return updatedDepartment;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Update failed';
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDepartmentFromDetails = async ({ name }) => {
    if (!detailsModal.department) {
      throw new Error('No department selected for editing.');
    }
    
    const deptId = detailsModal.department._id || detailsModal.department.id;
    
    try {
      const updateData = { tenantId: effectiveTenantId, name };
      const res = await api.put(`/api/edit-department/${deptId}`, updateData, { headers: authHeaders });

      const updatedDepartment = res?.data?.department || {
        ...detailsModal.department,
        name,
        updatedAt: new Date().toISOString()
      };

      setDepartments(prev => prev.map(d => {
        const id = d._id || d.id;
        return String(id) === String(deptId) ? updatedDepartment : d;
      }));

      setDetailsModal(prev => ({ ...prev, department: updatedDepartment }));
      return updatedDepartment;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Update failed';
      throw new Error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.departmentToDelete) return;
    const departmentId = deleteModal.departmentToDelete._id || deleteModal.departmentToDelete.id;
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    try {
      await api.delete(`/api/delete-department/${departmentId}`, { 
        data: { tenantId: effectiveTenantId }, 
        headers: authHeaders 
      });
      setDepartments(prev => prev.filter(d => String(d._id || d.id) !== String(departmentId)));
      closeDeleteModal();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Delete failed');
      closeDeleteModal();
    }
  };

  const handleConfigureDepartment = (department) => {
    closeModal();
    setTimeout(() => {
      openDetailsModal(department);
    }, 100);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <TenantAdminLayout>
      <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8 font-sans">
        
        {/* Upper Layout Banner Section */}
        <div className="mb-6 sm:mb-8 border-b border-gray-200 pb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 truncate">Academic Departments</h1>
              <p className="text-slate-500 text-xs sm:text-sm mt-0.5 max-w-2xl break-words">
                Manage core academic branches, review assigned faculty records, and update details.
              </p>
            </div>
            
            {/* Action Buttons Stack */}
            <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={fetchDepartments}
                className="flex-1 sm:flex-none justify-center px-3 sm:px-4 py-2.5 border border-slate-200 rounded-xl text-slate-700 bg-white hover:bg-slate-50 transition flex items-center gap-2 text-xs font-semibold shadow-xs min-h-[44px]"
              >
                <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="truncate">Refresh Roster</span>
              </button>
              
              <button
                type="button"
                onClick={openCreateModal}
                className="flex-1 sm:flex-none justify-center px-3 sm:px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center gap-2 text-xs font-semibold shadow-xs min-h-[44px]"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="truncate">Add Department</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading Progress State Indicator */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <div className="flex items-center gap-2.5 text-blue-600">
              <svg className="animate-spin h-5 w-5 text-current" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Loading department listings...</span>
          </div>
        )}

        {/* Error Boundary Notice layout boxes */}
        {!loading && error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2.5 text-rose-700 text-xs font-semibold break-words">
            <svg className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Error processing data: {error}</span>
          </div>
        )}

        {/* Empty Fallback State View */}
        {!loading && departments.length === 0 && !error && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 border-dashed max-w-xl mx-auto p-6">
            <div className="w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1">No Departments Registered</h3>
            <p className="text-slate-500 text-xs mb-5 max-w-sm mx-auto">
              Get started by adding your institution's first academic department.
            </p>
            <button
              type="button"
              onClick={openCreateModal}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-xl font-semibold text-xs transition shadow-xs"
            >
              Create Department
            </button>
          </div>
        )}

        {/* Academic Branch Listings Matrix Grid */}
        {!loading && departments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {departments.map((dept) => {
              const id = dept._id || dept.id;
              const facultyCount = dept.facultyCount || 0;
              
              return (
                <div 
                  key={id} 
                  className="bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col group hover:translate-y-[-2px]"
                >
                  <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-4">
                    <div className="flex items-start justify-between min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 truncate pr-2 group-hover:text-blue-600 transition-colors">
                        {dept.name}
                      </h3>
                    </div>
                    
                    {/* Detailed Faculty Count Indicator */}
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Faculty Allocation</span>
                      <span className="text-xs font-bold text-slate-800 mt-0.5">{facultyCount} Registered Members</span>
                    </div>
                    
                    {/* Footer Context Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 shrink-0 gap-2">
                      <div className="text-[10px] text-slate-400 font-medium truncate max-w-[120px]">
                        {dept.createdAt ? formatTime(dept.createdAt) : 'Recently Added'}
                      </div>
                      
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => openDetailsModal(dept)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition min-h-[38px] min-w-[38px] flex items-center justify-center"
                          title="View Full Profile"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={() => openEditModal(dept)}
                          className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition min-h-[38px] min-w-[38px] flex items-center justify-center"
                          title="Modify Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => openDeleteModal(dept)}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition min-h-[38px] min-w-[38px] flex items-center justify-center"
                          title="Remove Department"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Overlay Portals Layer */}
        <DepartmentModal
          isOpen={modalState.showCreateModal}
          onClose={closeModal}
          onSave={handleCreateSave}
          saving={modalState.saving}
          mode="create"
          onConfigure={handleConfigureDepartment}
        />

        <DepartmentModal
          isOpen={modalState.showEditModal}
          onClose={closeModal}
          initialData={modalState.editingDepartment}
          onSave={handleUpdateSave}
          saving={modalState.saving}
          mode="edit"
          onConfigure={handleConfigureDepartment}
        />

        <DepartmentDetailModal
          isOpen={detailsModal.isOpen}
          onClose={closeDetailsModal}
          department={detailsModal.department}
          faculty={[]}
          subjects={[]}
          onSaveDepartment={handleSaveDepartmentFromDetails}
          userRole={effectiveUserRole}
        />

        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDelete}
          department={deleteModal.departmentToDelete}
          deleting={deleteModal.deleting}
        />
      </div>
    </TenantAdminLayout>
  );
};

export default Department;